import { ValuesOf } from '../types';

const JOIN_FLOW_DATA_KEY = 'join_flow_data';

const JoinPerfEventTypes = {
    JOIN_MEETING_START: 'join_meeting_start',
    JOIN_WEB_STATIC_INIT_START: 'join_web_static_init_start',
    JOIN_WEB_STATIC_INIT_END: 'join_web_static_init_end',
    POST_BRIDGE_START: 'post_bridge_start',
    JOIN_REQ_START: 'join_req_start',
    JOIN_REQ_END: 'join_req_end',
    GET_BRIDGE_START: 'get_bridge_start',
    POST_BRIDGE_END: 'post_bridge_end',
    GET_BRIDGE_END: 'get_bridge_end',
    POST_MEETING_START: 'post_meetings_start',
    POST_MEETING_END: 'post_meetings_end',
    WS_SIG_CONNECT_REQ: 'ws_sig_connect_req',
    WS_SIG_CONNECT_RESP: 'ws_sig_connect_resp',
    WS_SIG_CREATE_REQ: 'ws_sig_create_req',
    WS_SIG_CREATE_RESP: 'ws_sig_create_resp',
    /** Peer connection establishment start */
    PEER_CONN_EST_START: 'peer_conn_est_start',
    /** Peer connection establishment end */
    PEER_CONN_EST_END: 'peer_conn_est_end',
    JOIN_MEETING_END: 'join_meeting_end',
} as const;

const JoinMeetingSource = {
    JOIN_BUTTON: 'JoinButton',
    PMI_TAB: 'PMITab',
    START_BUTTON: 'StartButton',
    PASSWORD_WINDOW: 'PasswordWindow',
    MEETING_LOCKED: 'MeetingLocked',
    MEETING_LIMITS_REACHED: 'MeetingLimitsReached',
    BROWSER_APP_SELECTION: 'BrowserAppSelection',
    CALENDAR_EVENT: 'CalendarEvent',
    LINK_SENT: 'LinkSent',
    WAITING_ROOM_WINDOW: 'WaitingRoom',
    JOIN_AUDIO_MODAL: 'JoinAudioSelection',
    HW_PERMISSIONS_MODAL: 'HWPermissionPrompt',
    SCREEN_SHARING_START: 'ScreenSharingStart',
    AV_PREVIEW: 'AVPreview',
} as const;

const JoinDirectCategory = {
    RECV_AUDIO: 'RecvAudio',
    RECV: 'Recv',
} as const;

const JoinIndirectCategory = {
    PASSWORD_REQUIRED: 'PasswordRequired',
    WAITING_ROOM: 'WaitingRoom',
    JOIN_AUDIO_SELECTION: 'JoinAudioSelection',
    SCREEN_SHARING_START: 'ScreenSharingStart',
    HW_PERMISSIONS_REQUIRED: 'HWPermissionsRequired',
    INVALID_MEETING_ID: 'InvalidMeetingId',
    AV_PREVIEW: 'AVPreview',
    MEETING_LOCKED: 'MeetingLocked',
    MEETING_LIMITS_REACHED: 'MeetingLimitsReached',
    SIMULTANEOUS_LIMIT: 'SimultaneousLimit',
    KICKED_OUT: 'ParticipantKickedOut',
    CAPABILITIES_CHECK_FAILED: 'CapabilitiesCheckFailed',
    E2EE_LIMITATIONS: 'E2EELimitations',
    LOGIN_REQUIRED: 'LoginRequired',
    COWORKER_LOGIN_REQUIRED: 'CoworkerLoginRequired',
    UNKNOWN_ERROR: 'UnknownError',
} as const;

type JoinBaseAttributes = {
    spanFlowId: string;
    flowId: string;
};

type JoinEventOutput = {
    event_type: JoinPerfEventTypeValues;
    attributes?: Record<string, unknown>;
    ts?: number;
};

type RequiredJoinMeetingStartInputAttributes = { joinSource: JoinMeetingSourceType };
type OptionalJoinMeetingStartInputAttributes = JoinBaseAttributes & {
    app: string;
};

type JoinMeetingStartInputAttributes = RequiredJoinMeetingStartInputAttributes &
    Partial<OptionalJoinMeetingStartInputAttributes>;

type RequiredJoinMeetingEndInputAttributes = {
    rcvRegion: string;
    configAudioAuto: boolean;
    configVideoOn: boolean;
    configScreensharingOn: boolean;
    browserType: string | undefined;
    browserVersion: string | undefined;
    app: string;
    appVersion: string;
    os: string;
    osVersion: string | undefined;
    network: string | undefined;
    hwCpuCores: number | null;
    hwMemory: number | string;
};
// these attributes are optional for input because they are calculated and added by join-flow-tracking itself
type OptionalJoinMeetingEndInputAttributes = JoinBaseAttributes &
    RcvMeetingAttributes & {
        durationMs: number;
        spanFlowDurationMs?: number;
        error?: boolean;
        errorCategory?: string;
        joinSource?: JoinMeetingSourceType;
        indirectCategory?: JoinIndirectCategoryType;
        directCategory?: JoinDirectCategoryType;
        embJwebVersion?: string;
    };

type JoinMeetingEndInputAttributes = RequiredJoinMeetingEndInputAttributes &
    Partial<OptionalJoinMeetingEndInputAttributes>;

type JoinNetworkEndInputAttributes = Partial<JoinBaseAttributes> &
    HttpRequestMetrics & { rcRequestId?: string };

type JoinNetworkStartEventTypes =
    | typeof JoinPerfEventTypes.POST_BRIDGE_START
    | typeof JoinPerfEventTypes.GET_BRIDGE_START
    | typeof JoinPerfEventTypes.JOIN_REQ_START
    | typeof JoinPerfEventTypes.POST_MEETING_START
    | typeof JoinPerfEventTypes.WS_SIG_CONNECT_REQ
    | typeof JoinPerfEventTypes.WS_SIG_CREATE_REQ
    | typeof JoinPerfEventTypes.PEER_CONN_EST_START;

type JoinNetworkEndEventTypes =
    | typeof JoinPerfEventTypes.POST_BRIDGE_END
    | typeof JoinPerfEventTypes.GET_BRIDGE_END
    | typeof JoinPerfEventTypes.JOIN_REQ_END
    | typeof JoinPerfEventTypes.POST_MEETING_END
    | typeof JoinPerfEventTypes.WS_SIG_CONNECT_RESP
    | typeof JoinPerfEventTypes.WS_SIG_CREATE_RESP
    | typeof JoinPerfEventTypes.PEER_CONN_EST_END;

type RestJoinEvents = Exclude<
    JoinPerfEventTypeValues,
    | typeof JoinPerfEventTypes.JOIN_MEETING_START
    | typeof JoinPerfEventTypes.JOIN_MEETING_END
    | JoinNetworkEndEventTypes
    | JoinNetworkStartEventTypes
>;

type JoinPendingRequest = {
    rcRequestId?: string;
    endEventType?: JoinNetworkEndEventTypes;
    metrics?: HttpRequestMetrics;
    eventTime?: number;
    hasResponseData?: boolean;
    hasPerformanceData?: boolean;
};

type JoinEventInput =
    | {
          eventType: typeof JoinPerfEventTypes.JOIN_MEETING_START;
          attributes: JoinMeetingStartInputAttributes;
          ts?: number;
      }
    | {
          eventType: typeof JoinPerfEventTypes.JOIN_MEETING_END;
          attributes: JoinMeetingEndInputAttributes;
          ts?: number;
      }
    | {
          eventType: JoinNetworkEndEventTypes;
          attributes?: JoinNetworkEndInputAttributes;
          ts?: number;
      }
    | {
          eventType: JoinNetworkStartEventTypes;
          attributes?: Partial<JoinBaseAttributes>;
          ts?: number;
      }
    | {
          eventType: RestJoinEvents;
          attributes?: Partial<JoinBaseAttributes>;
          ts?: number;
      };

type AnyJoinEvent = {
    eventType: JoinPerfEventTypeValues;
    attributes?: Record<string, unknown>;
    ts?: number;
};

type HttpRequestMetrics = {
    duration_ms?: number;
    dns_duration_ms?: number;
    tcp_duration_ms?: number;
    tls_duration_ms?: number;
    response_duration_ms?: number;
    response_code?: number;
    error?: boolean;
};

type RcvMeetingAttributes = {
    rcvBridgeId: string;
    rcvMeetingId: string;
    rcvSessionId: string;
};

type JoinPerfEventTypeValues = ValuesOf<typeof JoinPerfEventTypes>;

type JoinMeetingSourceType = ValuesOf<typeof JoinMeetingSource>;

type JoinIndirectCategoryType = ValuesOf<typeof JoinIndirectCategory>;

type JoinDirectCategoryType = ValuesOf<typeof JoinDirectCategory>;

export {
    JOIN_FLOW_DATA_KEY,
    JoinPerfEventTypes,
    JoinMeetingSource,
    JoinDirectCategory,
    JoinIndirectCategory,
    type JoinEventOutput,
    type JoinEventInput,
    type AnyJoinEvent,
    type JoinNetworkEndInputAttributes as JoinNetworkReqAttributes,
    type JoinBaseAttributes,
    type JoinNetworkStartEventTypes,
    type JoinNetworkEndEventTypes,
    type JoinMeetingStartInputAttributes as JoinMeetingStartAttributes,
    type RequiredJoinMeetingEndInputAttributes as JoinEnvAttributes,
    type JoinMeetingEndInputAttributes,
    type JoinPendingRequest,
    type HttpRequestMetrics,
    type RcvMeetingAttributes,
    type JoinPerfEventTypeValues,
    type JoinMeetingSourceType,
    type JoinIndirectCategoryType,
};
